import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE, useSelectedOperatingGroup } from '../hooks/useSelectedOperatingGroup';
import { isRestaurantOperatingContext } from '../utils/operatingContext';
import { extractApiErrorMessage } from '../utils/apiError';
import { formatRestaurantPayrollDate, formatRestaurantPayrollNumber } from '../utils/restaurantPayrollFormat';
import './RestaurantHallsPayrollPanel.css';

type Mapping = { sourceName: string; eventVenueId: number };
type Configuration = {
  id: number; enabled: boolean; effectiveYear: number; effectiveMonth: number; revision: number;
  orderRevenuePercent: number; additionalServicesProfitPercent: number;
  departmentIds: number[]; employmentIds: number[]; venueMappings: Mapping[];
};
export type RestaurantHallsReview = {
  configuration: Configuration;
  additionalServicesRevenue: number; additionalServicesDirectCosts: number; notes: string;
  calculation: {
    orderRevenue: number; additionalServicesProfit: number; orderRevenuePool: number;
    additionalServicesPool: number; totalPool: number;
  };
  includedOrders: Array<{ orderId: number; eventDate: string; sourceName: string; totalPrice: number; venueName: string }>;
  unmappedOrders: Array<{ orderId: number; sourceName: string; totalPrice: number }>;
  participants: Array<{ employmentId: number; employeeName: string; selection: string; workedHours: number; evaluationCoefficient: number; weight: number; amount: number }>;
};
type Options = {
  configuration: Configuration | null;
  departments: Array<{ departmentId: number; name: string }>;
  venues: Array<{ id: number; name: string }>;
  source: { additionalServicesRevenue: number; additionalServicesDirectCosts: number; notes: string } | null;
};
type Props = {
  groupId: string;
  year: number; month: number; version: number; exists: boolean; status: string; canEdit: boolean; busy: boolean;
  employees: Array<{ employmentId: number; fullName: string }>;
  review?: RestaurantHallsReview | null;
  runAction: (action: () => Promise<void>, message: string) => Promise<void>;
};
const currency = new Intl.NumberFormat('uk-UA', { style: 'currency', currency: 'UAH' });

export default function RestaurantHallsPayrollPanel(props: Props) {
  const { groupId, changedInAnotherTab } = useSelectedOperatingGroup();
  if (changedInAnotherTab) return <p role="alert">{OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE}</p>;
  if (!isRestaurantOperatingContext(groupId) || groupId !== props.groupId)
    return <p role="alert">Оберіть одну операційну групу для роботи із зарплатою ресторану.</p>;
  return <HallsPayrollWorkspace key={`${groupId}:${props.year}:${props.month}`} {...props} />;
}

function HallsPayrollWorkspace(props: Props) {
  const { year, month, version, status, canEdit, busy, exists, employees, review, runAction } = props;
  const [options, setOptions] = useState<Options | null>(null);
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [orderPercent, setOrderPercent] = useState('12');
  const [profitPercent, setProfitPercent] = useState('50');
  const [departments, setDepartments] = useState<number[]>([]);
  const [employments, setEmployments] = useState<number[]>([]);
  const [mappings, setMappings] = useState<Array<Mapping & { key: number }>>([]);
  const [revenue, setRevenue] = useState('0');
  const [costs, setCosts] = useState('0');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    let active = true;
    setOptions(null);
    setError('');
    api.get<Options>('/restaurant-payroll/halls/options', { params: { year, month } }).then(response => {
      if (!active) return;
      const data = response.data;
      setOptions(data);
      setEnabled(data.configuration?.enabled ?? false);
      setOrderPercent(String(data.configuration?.orderRevenuePercent ?? 12));
      setProfitPercent(String(data.configuration?.additionalServicesProfitPercent ?? 50));
      setDepartments(data.configuration?.departmentIds ?? []);
      setEmployments(data.configuration?.employmentIds ?? []);
      setMappings((data.configuration?.venueMappings ?? []).map((item, key) => ({ ...item, key })));
      setRevenue(String(data.source?.additionalServicesRevenue ?? 0));
      setCosts(String(data.source?.additionalServicesDirectCosts ?? 0));
      setNotes(data.source?.notes ?? '');
    }).catch((loadError: unknown) => {
      if (active) setError(extractApiErrorMessage(loadError, 'Не вдалося завантажити налаштування HALLS. Оновіть період.'));
    });
    return () => { active = false; };
  }, [year, month, version, employees]);
  const editable = canEdit && status === 'draft' && !busy && options !== null;
  const toggle = (ids: number[], id: number) => ids.includes(id) ? ids.filter(item => item !== id) : [...ids, id];

  return <section className="restaurant-halls salary-payroll__policy-settings">
    <h2>Фонд HALLS цієї групи</h2>
    <p>Тестові нарахування. Фонд не увімкнено автоматично. Явні учасники отримують лише HALLS замість звичайного методу.
      Джерело подій — лише завершені замовлення, які належать цій групі й мають явне зіставлення майданчика.</p>
    {error && <p role="alert">{error}</p>}
    {canEdit && status === 'draft' && options && <form onSubmit={event => {
      event.preventDefault();
      void runAction(async () => {
        await api.put('/restaurant-payroll/halls/configuration', {
          expectedConfigurationId: options.configuration?.id ?? 0, effectiveYear: year, effectiveMonth: month,
          enabled, orderRevenuePercent: Number(orderPercent), additionalServicesProfitPercent: Number(profitPercent),
          departmentIds: departments, employmentIds: employments,
          venueMappings: mappings.map(({ sourceName, eventVenueId }) => ({ sourceName, eventVenueId })),
        });
      }, 'Нову ревізію HALLS збережено з вибраного місяця.');
    }}>
      <fieldset disabled={!editable}>
        <label className="restaurant-halls__checkbox"><input type="checkbox" checked={enabled} onChange={event => setEnabled(event.target.checked)} />Увімкнути HALLS з вибраного місяця</label>
        <label>Відсоток виторгу подій<input required type="number" min="0" max="100" step="0.0001" value={orderPercent} onChange={event => setOrderPercent(event.target.value)} /></label>
        <label>Відсоток прибутку додаткових послуг<input required type="number" min="0" max="100" step="0.0001" value={profitPercent} onChange={event => setProfitPercent(event.target.value)} /></label>
        <h3>Явні підрозділи-учасники</h3>
        {options.departments.map(item => <label className="restaurant-halls__checkbox" key={item.departmentId}>
          <input type="checkbox" checked={departments.includes(item.departmentId)} onChange={() => setDepartments(toggle(departments, item.departmentId))} />{item.name}
        </label>)}
        {departments.filter(id => !options.departments.some(item => item.departmentId === id)).map(id => <label className="restaurant-halls__checkbox" key={id}>
          <input type="checkbox" checked onChange={() => setDepartments(previous => previous.filter(item => item !== id))} />Недоступний у цьому місяці підрозділ #{id} — приберіть із нової ревізії
        </label>)}
        <h3>Або окремі працевлаштування</h3>
        <p>Вибір підрозділу та працівника одночасно не подвоює нарахування. Години враховуються лише у межах локальних дат роботи й членства підрозділу.</p>
        {employees.map(item => <label className="restaurant-halls__checkbox" key={item.employmentId}>
          <input type="checkbox" checked={employments.includes(item.employmentId)} onChange={() => setEmployments(toggle(employments, item.employmentId))} />{item.fullName}
        </label>)}
        {employments.filter(id => !employees.some(item => item.employmentId === id)).map(id => <label className="restaurant-halls__checkbox" key={id}>
          <input type="checkbox" checked onChange={() => setEmployments(previous => previous.filter(item => item !== id))} />Недоступне у цьому місяці працевлаштування #{id} — приберіть із нової ревізії
        </label>)}
        <h3>Явні назви майданчиків</h3>
        {mappings.map(item => <div className="restaurant-halls__mapping" key={item.key}>
          <label>Назва в замовленні<input required maxLength={300} value={item.sourceName} onChange={event => setMappings(previous => previous.map(row => row.key === item.key ? { ...row, sourceName: event.target.value } : row))} /></label>
          <label>Майданчик групи<select required value={item.eventVenueId || ''} onChange={event => setMappings(previous => previous.map(row => row.key === item.key ? { ...row, eventVenueId: Number(event.target.value) } : row))}>
            <option value="">Оберіть майданчик</option>
            {options.venues.map(venue => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
          </select></label>
          <button type="button" onClick={() => setMappings(previous => previous.filter(row => row.key !== item.key))}>Прибрати зіставлення</button>
        </div>)}
        <button type="button" onClick={() => setMappings(previous => [...previous, { key: Math.max(-1, ...previous.map(item => item.key)) + 1, sourceName: '', eventVenueId: 0 }])}>Додати зіставлення</button>
        <button type="submit">Зберегти налаштування HALLS</button>
      </fieldset>
    </form>}
    {canEdit && status === 'draft' && exists && options?.configuration?.enabled && <form onSubmit={event => {
      event.preventDefault();
      void runAction(async () => {
        await api.put(`/restaurant-payroll/${year}/${month}/halls/sources`, {
          version, additionalServicesRevenue: Number(revenue), additionalServicesDirectCosts: Number(costs), notes,
        });
      }, 'Джерела додаткових послуг HALLS підтверджено.');
    }}>
      <fieldset disabled={!editable}>
        <legend>Підтвердьте додаткові послуги (навіть нульові)</legend>
        <label>Виторг додаткових послуг, ₴<input required type="number" min="0" step="0.01" value={revenue} onChange={event => setRevenue(event.target.value)} /></label>
        <label>Прямі витрати додаткових послуг, ₴<input required type="number" min="0" step="0.01" value={costs} onChange={event => setCosts(event.target.value)} /></label>
        <label>Примітка до джерел HALLS<input maxLength={2000} value={notes} onChange={event => setNotes(event.target.value)} /></label>
        <button type="submit">Підтвердити джерела HALLS</button>
      </fieldset>
    </form>}
    {!review && !error && <p>Розрахунок HALLS ще не доступний: перевірте явне налаштування, джерела й проблеми розрахунку нижче.</p>}
    {review && !error && <>
      <h3>{status === 'draft' ? 'Перевірка фонду перед погодженням' : 'Незмінний погоджений знімок HALLS'}</h3>
      <p>Ревізія {review.configuration.revision} з {String(review.configuration.effectiveMonth).padStart(2, '0')}.{review.configuration.effectiveYear}:
        події {formatRestaurantPayrollNumber(review.configuration.orderRevenuePercent)}%, прибуток додаткових послуг {formatRestaurantPayrollNumber(review.configuration.additionalServicesProfitPercent)}%.</p>
      <p>Виторг подій {currency.format(review.calculation.orderRevenue)} → {currency.format(review.calculation.orderRevenuePool)}.
        Додаткові послуги: {currency.format(review.additionalServicesRevenue)} − {currency.format(review.additionalServicesDirectCosts)} →
        {currency.format(review.calculation.additionalServicesPool)}. Фонд після обмеження нулем: <strong>{currency.format(review.calculation.totalPool)}</strong>.</p>
      {review.notes && <p>{review.notes}</p>}
      <h3>Включені замовлення</h3>
      {review.includedOrders.map(order => <p key={order.orderId}>#{order.orderId} · {formatRestaurantPayrollDate(order.eventDate)} · {order.sourceName} → {order.venueName} · {currency.format(order.totalPrice)}</p>)}
      {!review.includedOrders.length && <p>Немає включених замовлень.</p>}
      <h3>Не включені: немає дійсного зіставлення</h3>
      {review.unmappedOrders.map(order => <p key={order.orderId}>#{order.orderId} · {order.sourceName} · {currency.format(order.totalPrice)}</p>)}
      {!review.unmappedOrders.length && <p>Немає незiставлених замовлень цієї групи.</p>}
      <h3>Розподіл до бонусів та штрафів</h3>
      {review.participants.map(item => <p key={item.employmentId}>{item.employeeName} · {item.selection === 'employment' ? 'явне працевлаштування' : 'явний підрозділ'} ·
        {formatRestaurantPayrollNumber(item.workedHours)} год × {formatRestaurantPayrollNumber(item.evaluationCoefficient)} = {formatRestaurantPayrollNumber(item.weight)} · {currency.format(item.amount)}</p>)}
    </>}
  </section>;
}
